import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock,
  CreditCard,
  ImagePlus,
  Layers,
  PartyPopper,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ApiError, apiBase } from "@/api/client";
import {
  useCancelPlanChange,
  useCreateCheckoutSession,
  useCurrentUser,
  useOrganization,
  useOverageDetail,
  usePublicPlans,
  useRequestPlanChange,
  useSubscriptionStatus,
  useUpdateOrgBranding,
  useUploadOrgLogo,
} from "@/api/hooks";
import type {
  OrganizationDetail,
  PublicPlanTierResponse,
  QuoteRenderMode,
  SubscriptionStatusResponse,
} from "@/api/types";
import { OrgConfigBundles } from "@/components/OrgConfigBundles";
import { PhoneInput } from "@/components/PhoneInput";
import { formatPhoneForDisplay } from "@/lib/phoneFormat";

/** Tier names that ?plan= can resolve to. Anything else gets ignored. */
const VALID_REQUESTED_TIERS = new Set(["free", "pro", "crew"]);

/** Org-side plan & billing dashboard. Visible to ``org_admin`` and
 *  ``pencilout_admin``. Shows current tier + usage meter; lets the
 *  org admin start a Stripe Checkout (initial subscribe) or stage a
 *  plan change (applied at end of current billing period — the
 *  "no partial months" rule). */
export function PlanBillingPage() {
  const me = useCurrentUser();
  const orgId = me.data?.organization_id ?? null;

  if (me.isLoading) {
    return <p className="text-sm text-graphite-soft">Loading…</p>;
  }
  if (!me.data) {
    return null;
  }
  if (!orgId) {
    return (
      <div className="space-y-3">
        <h1 className="font-mono text-2xl font-bold tracking-tight">Plan &amp; billing</h1>
        <p className="max-w-prose text-sm text-graphite-soft">
          You don't currently have an organization context. PencilOut staff should pick an org from
          the Organizations page to view its plan and billing state.
        </p>
      </div>
    );
  }
  return <PlanBillingForOrg orgId={orgId} />;
}

function PlanBillingForOrg({ orgId }: { orgId: string }) {
  // Re-read the current user inside this scope (the parent
  // PlanBillingPage's ``me`` is out of scope here). React-query
  // caches the same query so this isn't a duplicate fetch — just
  // a second consumer of the cached value.
  const me = useCurrentUser();
  const status = useSubscriptionStatus(orgId);
  const publicPlans = usePublicPlans();
  const org = useOrganization(orgId);
  const [searchParams] = useSearchParams();

  // ?plan=<tier> arrives via the marketing-page CTA → signup →
  // magic-link → /billing redirect chain. Used to highlight the
  // requested tier with a "Continue your upgrade" banner.
  const rawRequested = searchParams.get("plan");
  const requestedTier =
    rawRequested && VALID_REQUESTED_TIERS.has(rawRequested) ? rawRequested : null;

  // ?checkout=success is set by the Stripe Checkout return URL. The
  // success-banner component handles its own dismissal state and
  // also re-fetches the subscription status (the webhook may still
  // be propagating).
  const justCheckedOut = searchParams.get("checkout") === "success";

  if (status.isLoading || publicPlans.isLoading) {
    return <p className="text-sm text-graphite-soft">Loading plan…</p>;
  }
  if (status.error) {
    return (
      <p className="text-sm text-red-pencil">
        {status.error instanceof ApiError ? status.error.detail : "Could not load subscription."}
      </p>
    );
  }
  if (!status.data || !publicPlans.data) {
    return null;
  }

  // Comped orgs are on a Veridon-internal pilot tier — no quota,
  // no Stripe, no upgrade pressure anywhere. The Plan & billing
  // page shows a calm "you're on the pilot" card and skips
  // PlanOptions / RequestedUpgradeBanner / OverageCard entirely.
  // To leave the pilot, support flips them back via the staff
  // /organizations comp toggle (it's not a self-service action).
  const isComped = status.data.plan_tier === "comped";

  // Only show the upgrade-prompt banner if the requested tier is
  // actually different from where they are. Avoids "Continue your
  // upgrade to Pro" when they're already on Pro. Suppressed entirely
  // for comped — pilots aren't supposed to see upgrade pressure.
  const showUpgradePrompt =
    !isComped &&
    requestedTier !== null &&
    requestedTier !== status.data.plan_tier &&
    requestedTier !== "free";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-mono text-2xl font-bold tracking-tight">Plan &amp; billing</h1>
        <p className="mt-1 max-w-prose text-sm text-graphite-soft">
          {isComped
            ? "You're on a comped pilot tier with PencilOut. No quota, no charges. We'll be in touch before this changes."
            : "Manage your tier and watch this cycle's usage. Plan changes apply at the end of the current billing period — no partial-month charges."}
        </p>
      </div>

      {justCheckedOut && !isComped && (
        <UpgradeSuccessBanner orgId={orgId} status={status.data} hasLogo={org.data?.has_logo} />
      )}
      {showUpgradePrompt && (
        <RequestedUpgradeBanner
          orgId={orgId}
          requestedTier={requestedTier}
          plans={publicPlans.data}
          hasSubscription={status.data.has_subscription}
        />
      )}
      {isComped ? (
        <CompedPlanCard />
      ) : (
        <>
          <PendingChangeBanner orgId={orgId} status={status.data} />
          <BillingLeakWarning status={status.data} />
          <CurrentPlanCard status={status.data} plans={publicPlans.data} />
          <OverageCard orgId={orgId} role={me.data?.role ?? null} />
        </>
      )}
      {/* Branding moved to its own /settings page. The page-level
          quote-presentation surface used to live here, but burying
          "what does my company look like to homeowners" under
          Plan & billing made it undiscoverable — and worse, it
          disappeared entirely on comped orgs (which hide most of
          this page). /settings is now the system-of-record path. */}
      {!isComped && <PlanOptions orgId={orgId} status={status.data} plans={publicPlans.data} />}
    </div>
  );
}

/** Quiet card shown in place of the Current Plan / Overage / Plan
 *  Options stack when the org is on the Veridon-comped pilot tier.
 *  No CTA, no usage meter, no upgrade pressure — just confirmation
 *  that they're on the pilot and what that means. */
function CompedPlanCard() {
  return (
    <div className="rounded-lg border border-pencil-yellow bg-pencil-yellow-soft p-5">
      <div className="flex items-start gap-3">
        <PartyPopper className="mt-0.5 h-5 w-5 flex-none text-graphite" aria-hidden />
        <div className="flex-1 text-sm">
          <p className="font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft">
            Pilot — comped
          </p>
          <p className="mt-2">
            You're using PencilOut as a Veridon pilot. There's no quote quota and no charges during
            the pilot.
          </p>
          <p className="mt-2 text-graphite-soft">
            When the pilot ends, we'll reach out to talk through which plan fits your volume. No
            card needed in the meantime.
          </p>
        </div>
      </div>
    </div>
  );
}

function RequestedUpgradeBanner({
  orgId,
  requestedTier,
  plans,
  hasSubscription,
}: {
  orgId: string;
  requestedTier: string;
  plans: PublicPlanTierResponse[];
  hasSubscription: boolean;
}) {
  const checkout = useCreateCheckoutSession(orgId);
  const change = useRequestPlanChange(orgId);
  const [error, setError] = useState<string | null>(null);

  const targetPlan = plans.find((p) => p.tier === requestedTier);
  if (!targetPlan) return null;

  async function handleContinue() {
    setError(null);
    try {
      if (!hasSubscription) {
        const result = await checkout.mutateAsync({
          plan_tier: requestedTier,
          success_url: `${window.location.origin}/billing?checkout=success`,
          cancel_url: `${window.location.origin}/billing`,
        });
        window.location.href = result.url;
        return;
      }
      await change.mutateAsync({ plan_tier: requestedTier });
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    }
  }

  return (
    <div className="rounded-lg border border-pencil-yellow bg-pencil-yellow-soft p-4">
      <div className="flex flex-wrap items-center gap-3">
        <ArrowRight className="h-5 w-5 flex-none text-graphite" aria-hidden />
        <div className="flex-1 text-sm">
          <p className="font-semibold">Welcome! Continue your upgrade to {targetPlan.name}.</p>
          <p className="text-graphite-soft">
            ${(targetPlan.price_monthly_cents / 100).toFixed(0)} / month ·{" "}
            {targetPlan.included_walkthroughs} quotes included.
            {hasSubscription
              ? " Change applies at the end of your current cycle."
              : " You'll go through Stripe Checkout next."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void handleContinue()}
          disabled={checkout.isPending || change.isPending}
          className="rounded bg-graphite px-4 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-40"
        >
          {checkout.isPending || change.isPending ? "Working…" : `Subscribe to ${targetPlan.name}`}
        </button>
      </div>
      {error && <p className="mt-2 text-xs text-red-pencil">{error}</p>}
    </div>
  );
}

// ---------------------------------------------------------------
// Cards
// ---------------------------------------------------------------

function CurrentPlanCard({
  status,
  plans,
}: {
  status: SubscriptionStatusResponse;
  plans: PublicPlanTierResponse[];
}) {
  const currentPlan = plans.find((p) => p.tier === status.plan_tier);
  const planLabel =
    currentPlan?.name ??
    (status.plan_tier
      ? status.plan_tier.charAt(0).toUpperCase() + status.plan_tier.slice(1)
      : "Legacy (no plan tier set)");

  const used = status.walkthroughs_used;
  const included = status.walkthroughs_included;
  const over = status.walkthroughs_over;
  const pct = included > 0 ? Math.min(100, Math.round((used / included) * 100)) : 0;

  return (
    <div className="rounded-lg border border-rule bg-paper-warm p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-graphite-soft" aria-hidden />
            <h2 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
              Current plan
            </h2>
          </div>
          <p className="mt-2 font-mono text-2xl font-bold tracking-tight">{planLabel}</p>
          {currentPlan && (
            <p className="mt-1 text-sm text-graphite-soft">
              ${(currentPlan.price_monthly_cents / 100).toFixed(0)} / month
              {currentPlan.overage_cents_per_walkthrough != null
                ? ` · $${(currentPlan.overage_cents_per_walkthrough / 100).toFixed(
                    0,
                  )} per extra quote`
                : ""}
            </p>
          )}
        </div>
        <div className="text-right">
          <p className="font-mono text-[11px] uppercase tracking-wider text-graphite-soft">
            This cycle
          </p>
          <p className="mt-1 font-mono text-lg font-bold tabular-nums">
            {used} <span className="text-graphite-soft">of</span> {included}
          </p>
          <p className="mt-0.5 font-mono text-[11px] text-graphite-faint">
            resets {new Date(status.period_end).toLocaleDateString()}
          </p>
        </div>
      </div>

      {included > 0 && (
        <div className="mt-4">
          <div className="h-2 w-full overflow-hidden rounded-full bg-paper">
            <div
              className={`h-full ${pct >= 100 ? "bg-red-pencil" : "bg-graphite"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          {over > 0 && (
            <p className="mt-2 font-mono text-xs text-graphite-soft">
              {over} over the included quota — bills as overage at end of cycle.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** Live overage cost detail — only fetched + rendered for
 *  ``org_admin`` and ``pencilout_admin`` callers per Brad's
 *  requirement. Hidden entirely for ``org_member`` so we don't
 *  even hint at the cost surface. Sub-card lists each cycle
 *  walkthrough with an "OVERAGE" tag on the rows past included
 *  quota — sanity check for the contractor before the Stripe
 *  metered bill posts. */
function OverageCard({
  orgId,
  role,
}: {
  orgId: string;
  role: string | null;
}) {
  const isOrgAdmin = role === "org_admin" || role === "pencilout_admin";
  const detail = useOverageDetail(orgId, { enabled: isOrgAdmin });

  if (!isOrgAdmin) return null; // org_member never sees this card.
  if (detail.isLoading) {
    return (
      <div className="rounded-lg border border-rule bg-paper-warm p-5">
        <p className="font-mono text-xs text-graphite-soft">Loading overage detail…</p>
      </div>
    );
  }
  if (detail.isError || !detail.data) {
    return null; // Silently absent if the fetch fails — non-critical.
  }
  const d = detail.data;

  const accrued = (d.accrued_overage_cents / 100).toFixed(2);
  const rateDollars = (d.rate_cents_per_walkthrough / 100).toFixed(0);
  const overageRows = d.walkthroughs.filter((w) => w.counts_as_overage);

  return (
    <div className="rounded-lg border border-rule bg-paper-warm p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-graphite-soft" aria-hidden />
            <h2 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
              Overage this cycle
            </h2>
          </div>
          <p className="mt-2 font-mono text-2xl font-bold tracking-tight tabular-nums">
            ${accrued}
          </p>
          <p className="mt-1 text-sm text-graphite-soft">
            {d.over === 0
              ? "No overage quotes this cycle yet."
              : `${d.over} quote${d.over === 1 ? "" : "s"} over the included ${d.included} · $${rateDollars} per overage`}
          </p>
        </div>
        <div className="text-right">
          <p className="font-mono text-[11px] uppercase tracking-wider text-graphite-soft">
            Bills at cycle close
          </p>
          <p className="mt-1 font-mono text-xs text-graphite-faint">
            {new Date(d.period_end).toLocaleDateString()}
          </p>
        </div>
      </div>

      {overageRows.length > 0 && (
        <div className="mt-4 border-t border-rule pt-4">
          <h3 className="font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft">
            Overage quotes
          </h3>
          <ul className="mt-2 divide-y divide-rule/60 text-sm">
            {overageRows.map((w, i) => (
              <li
                key={`${w.walkthrough_id ?? "legacy"}-${i}`}
                className="flex items-center justify-between gap-3 py-2"
              >
                <span className="min-w-0 flex-1 truncate">
                  {w.job_title ?? <span className="text-graphite-faint">(deleted quote)</span>}
                </span>
                <span className="font-mono text-xs tabular-nums text-graphite-soft">
                  {new Date(w.finalized_at).toLocaleDateString()}
                </span>
                <span className="font-mono text-xs font-semibold tabular-nums">${rateDollars}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 font-mono text-[11px] text-graphite-faint">
            These will roll up into a single Stripe metered usage line at cycle close. The dollar
            amount above is the running total.
          </p>
        </div>
      )}
    </div>
  );
}

function PendingChangeBanner({
  orgId,
  status,
}: {
  orgId: string;
  status: SubscriptionStatusResponse;
}) {
  const cancel = useCancelPlanChange(orgId);
  if (!status.pending_plan_tier || !status.pending_plan_change_at) {
    return null;
  }
  const target = status.pending_plan_tier;
  const when = new Date(status.pending_plan_change_at).toLocaleDateString();
  return (
    <div className="flex items-center gap-3 rounded-lg border border-pencil-yellow bg-pencil-yellow-soft p-4">
      <Clock className="h-5 w-5 flex-none text-graphite" aria-hidden />
      <div className="flex-1 text-sm">
        <p className="font-semibold">
          Switching to {target.charAt(0).toUpperCase() + target.slice(1)} on {when}
        </p>
        <p className="text-graphite-soft">
          Plan changes happen at end of cycle. You can cancel any time before this date.
        </p>
      </div>
      <button
        type="button"
        onClick={() => cancel.mutate()}
        disabled={cancel.isPending}
        className="rounded border border-graphite bg-paper px-3 py-1.5 font-mono text-sm font-semibold text-graphite transition hover:bg-graphite hover:text-paper disabled:opacity-40"
      >
        {cancel.isPending ? "Canceling…" : "Cancel change"}
      </button>
    </div>
  );
}

function BillingLeakWarning({ status }: { status: SubscriptionStatusResponse }) {
  const isPaid = status.plan_tier && status.plan_tier !== "free";
  if (!isPaid || status.has_metered_item) return null;
  return (
    <div className="flex items-start gap-3 rounded-lg border border-red-pencil bg-paper-warm p-4">
      <AlertTriangle className="h-5 w-5 flex-none text-red-pencil" aria-hidden />
      <div className="text-sm">
        <p className="font-semibold text-red-pencil">Billing setup incomplete</p>
        <p className="mt-1 text-graphite-soft">
          You're on a paid tier but Stripe doesn't have a metered subscription item recorded.
          Overage quotes won't bill correctly. PencilOut support can re-sync — please reach out.
        </p>
      </div>
    </div>
  );
}

function PlanOptions({
  orgId,
  status,
  plans,
}: {
  orgId: string;
  status: SubscriptionStatusResponse;
  plans: PublicPlanTierResponse[];
}) {
  // Sort by sort_order; current plan first if not already.
  const sorted = [...plans].sort((a, b) => a.sort_order - b.sort_order);
  const onPaidPlan = status.plan_tier && status.plan_tier !== "free";

  return (
    <div className="rounded-lg border border-rule bg-paper-warm p-5">
      <h2 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
        Available plans
      </h2>
      <p className="mt-1 max-w-prose text-xs text-graphite-soft">
        Switch tier — change applies at the end of your current cycle.
      </p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {sorted.map((plan) => (
          <PlanOptionCard
            key={plan.tier}
            orgId={orgId}
            plan={plan}
            isCurrent={plan.tier === status.plan_tier}
            isPending={plan.tier === status.pending_plan_tier}
            onPaidPlan={Boolean(onPaidPlan)}
            hasSubscription={status.has_subscription}
          />
        ))}
      </div>
    </div>
  );
}

function PlanOptionCard({
  orgId,
  plan,
  isCurrent,
  isPending,
  onPaidPlan,
  hasSubscription,
}: {
  orgId: string;
  plan: PublicPlanTierResponse;
  isCurrent: boolean;
  isPending: boolean;
  onPaidPlan: boolean;
  hasSubscription: boolean;
}) {
  const checkout = useCreateCheckoutSession(orgId);
  const change = useRequestPlanChange(orgId);
  const [error, setError] = useState<string | null>(null);

  // Mode logic:
  //   - On Free without sub + click Pro/Crew → Stripe Checkout.
  //   - On Free without sub + click Free → no-op (already there).
  //   - On paid → switching tiers = stage plan-change (cron applies).
  //   - On paid → click Free = stage cancel-at-period-end via plan-change.
  async function handleSwitch() {
    setError(null);
    if (isCurrent) return;
    try {
      if (!hasSubscription && plan.tier !== "free") {
        const result = await checkout.mutateAsync({
          plan_tier: plan.tier,
          success_url: `${window.location.origin}/billing?checkout=success`,
          cancel_url: `${window.location.origin}/billing?checkout=cancel`,
        });
        window.location.href = result.url;
        return;
      }
      if (onPaidPlan) {
        await change.mutateAsync({ plan_tier: plan.tier });
        return;
      }
      // Edge: org is on Free without sub and clicked Free → nothing to do.
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    }
  }

  const ctaLabel = (() => {
    if (isCurrent) return "Current plan";
    if (isPending) return "Pending switch";
    if (!hasSubscription && plan.tier !== "free") return "Subscribe";
    if (onPaidPlan && plan.tier === "free") return "Cancel at period end";
    return "Switch at period end";
  })();

  const disabled = isCurrent || isPending || checkout.isPending || change.isPending;

  return (
    <div
      className={`flex flex-col rounded border p-4 ${
        isCurrent ? "border-graphite bg-paper" : "border-rule bg-paper hover:border-graphite"
      } transition`}
    >
      <div className="flex items-baseline justify-between">
        <h3 className="font-mono text-sm font-bold uppercase tracking-wider">{plan.name}</h3>
        {isCurrent && (
          <span className="inline-flex items-center gap-1 rounded bg-pencil-yellow-soft px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-graphite">
            <CheckCircle2 className="h-3 w-3" aria-hidden />
            current
          </span>
        )}
      </div>
      <p className="mt-2 font-mono text-2xl font-bold tabular-nums">
        ${(plan.price_monthly_cents / 100).toFixed(0)}
        <span className="ml-1 text-sm font-normal text-graphite-soft">/ mo</span>
      </p>
      <p className="mt-1 text-xs text-graphite-soft">{plan.tagline}</p>

      <ul className="mt-3 flex-1 space-y-1 text-xs">
        <li className="font-mono">{plan.included_walkthroughs} quotes included</li>
        {plan.overage_cents_per_walkthrough != null && (
          <li className="font-mono text-graphite-soft">
            ${(plan.overage_cents_per_walkthrough / 100).toFixed(0)} / extra quote
          </li>
        )}
        {plan.features.slice(0, 3).map((f) => (
          <li key={f} className="text-graphite-soft">
            {f}
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={() => void handleSwitch()}
        disabled={disabled}
        className={`mt-4 rounded px-3 py-2 font-mono text-sm font-semibold transition ${
          isCurrent
            ? "border border-rule text-graphite-soft"
            : "bg-graphite text-paper hover:bg-graphite-soft disabled:opacity-40"
        }`}
      >
        {checkout.isPending || change.isPending ? "Working…" : ctaLabel}
      </button>

      {error && <p className="mt-2 text-xs text-red-pencil">{error}</p>}
    </div>
  );
}

// ---------------------------------------------------------------
// Branding card — logo upload + business contact + quote defaults
// ---------------------------------------------------------------

/** Renders the contractor's full branding stack: logo widget,
 *  business contact (phone/email/website/address), brand color,
 *  default quote terms / deposit %, reminder cadence, email
 *  templates. Exported so :class:`ContractorSettingsPage` can
 *  embed it without copying ~640 lines of form state.
 *
 *  Used to live on /billing — that buried it under "how I pay
 *  PencilOut," made it undiscoverable to first-time contractors,
 *  and disappeared entirely on comped pilot orgs (which hide most
 *  of the billing page). Now lives at /settings; PlanBillingPage
 *  stays focused on plan + usage + invoices.
 *
 *  Branded PDFs (Pro/Crew) require a logo, so we nudge harder
 *  when the org is on a paid tier with no logo on file. */
export function BrandingCard({
  orgId,
  org,
  status,
}: {
  orgId: string;
  org: OrganizationDetail | null;
  status: SubscriptionStatusResponse;
}) {
  const upload = useUploadOrgLogo(orgId);
  const inputRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [bustKey, setBustKey] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Legacy deep-link fallback — older emails / cached pages may
  // still link to ``/some-route#branding``. React Router v6
  // doesn't auto-scroll to hash fragments, so we do it ourselves
  // on mount.
  useEffect(() => {
    if (window.location.hash === "#branding" && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const hasLogo = org?.has_logo ?? false;
  const onPaid = status.plan_tier === "pro" || status.plan_tier === "crew";
  const branded = onPaid; // Free PDFs aren't branded; copy below reflects that.

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(null);
    upload.mutate(f, {
      onSuccess: () => setBustKey((k) => k + 1),
      onError: (err) => setError(err instanceof Error ? err.message : String(err)),
    });
    e.target.value = "";
  }

  return (
    <div ref={cardRef} id="branding" className="rounded-lg border border-rule bg-paper-warm p-5">
      <div className="flex items-center gap-2">
        <ImagePlus className="h-4 w-4 text-graphite-soft" aria-hidden />
        <h2 className="font-mono text-sm font-semibold uppercase tracking-wider text-graphite-soft">
          Branding
        </h2>
      </div>
      <p className="mt-2 max-w-prose text-sm text-graphite-soft">
        {branded
          ? "Upload your logo so it appears on quote PDFs and the public quote view your homeowners see. PNG, JPEG, SVG, or WebP — under 2 MB."
          : "Pro and Crew quote PDFs include your logo at the top. Upload it now so it's ready when you upgrade."}
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-4">
        <div className="flex h-20 w-32 items-center justify-center overflow-hidden rounded border border-rule bg-paper">
          {hasLogo ? (
            <img
              src={`${apiBase}/organizations/${orgId}/logo?v=${bustKey}`}
              alt="Your logo"
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <span className="font-mono text-[11px] text-graphite-faint">no logo</span>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={upload.isPending}
            className="rounded bg-graphite px-4 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-40"
          >
            {upload.isPending ? "Uploading…" : hasLogo ? "Replace logo" : "Upload logo"}
          </button>
          {hasLogo && onPaid && (
            <span className="inline-flex items-center gap-1 self-center font-mono text-xs text-graphite-soft">
              <CheckCircle2 className="h-3.5 w-3.5 text-graphite" aria-hidden />
              Showing on your branded PDFs
            </span>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml,image/webp"
          className="hidden"
          onChange={handleFile}
        />
      </div>

      {error && <p className="mt-3 text-xs text-red-pencil">{error}</p>}

      <BrandingDefaultsForm orgId={orgId} org={org} />
    </div>
  );
}

// ---------------------------------------------------------------
// Branding defaults sub-form — tagline, default terms, deposit pct.
// ---------------------------------------------------------------

/** Three-field form that PATCHes /organizations/{id}/branding. Sits
 *  inside :func:`BrandingCard` under the logo widget. Save is debounced
 *  to a click — auto-save on every keystroke would spam the audit log,
 *  and contractors expect to "save" before walking away from the field.
 *
 *  Empty inputs map to ``null`` server-side (= clear the value). The
 *  deposit field accepts whole-percent integers (0–100) for usability
 *  and we divide by 100 on submit. */
function BrandingDefaultsForm({
  orgId,
  org,
}: {
  orgId: string;
  org: OrganizationDetail | null;
}) {
  const update = useUpdateOrgBranding(orgId);
  const [tagline, setTagline] = useState<string>(org?.quote_header_tagline ?? "");
  const [terms, setTerms] = useState<string>(org?.default_quote_terms ?? "");
  const [depositPctText, setDepositPctText] = useState<string>(
    org?.default_deposit_pct != null ? Math.round(org.default_deposit_pct * 100).toString() : "",
  );
  const [reminderCadenceText, setReminderCadenceText] = useState<string>(
    org?.quote_reminder_cadence_days != null ? org.quote_reminder_cadence_days.toString() : "3",
  );
  const [reminderMaxText, setReminderMaxText] = useState<string>(
    org?.quote_reminder_max != null ? org.quote_reminder_max.toString() : "3",
  );
  const [phone, setPhone] = useState<string>(formatPhoneForDisplay(org?.business_phone));
  const [businessEmail, setBusinessEmail] = useState<string>(org?.business_email ?? "");
  const [website, setWebsite] = useState<string>(org?.business_website ?? "");
  const [address, setAddress] = useState<string>(org?.business_address ?? "");
  const [brandColor, setBrandColor] = useState<string>(org?.brand_primary_color ?? "");
  const [emailSubjectTpl, setEmailSubjectTpl] = useState<string>(
    org?.quote_email_subject_template ?? "",
  );
  const [emailBodyTpl, setEmailBodyTpl] = useState<string>(org?.quote_email_body_template ?? "");
  const [outputLanguage, setOutputLanguage] = useState<string>(org?.output_language ?? "en");
  const [defaultRenderMode, setDefaultRenderMode] = useState<QuoteRenderMode>(
    org?.default_render_mode ?? "itemized",
  );
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-sync local state when the org refetches (e.g. logo upload
  // invalidated the cache and the new payload landed). React only on
  // org.id transitions + first non-null payload to avoid clobbering
  // mid-edit state on every cache invalidation.
  const lastSyncedId = useRef<string | null>(null);
  useEffect(() => {
    if (!org) return;
    if (lastSyncedId.current === org.id) return;
    lastSyncedId.current = org.id;
    setTagline(org.quote_header_tagline ?? "");
    setTerms(org.default_quote_terms ?? "");
    setDepositPctText(
      org.default_deposit_pct != null ? Math.round(org.default_deposit_pct * 100).toString() : "",
    );
    setReminderCadenceText(org.quote_reminder_cadence_days.toString());
    setReminderMaxText(org.quote_reminder_max.toString());
    setPhone(formatPhoneForDisplay(org.business_phone));
    setBusinessEmail(org.business_email ?? "");
    setWebsite(org.business_website ?? "");
    setAddress(org.business_address ?? "");
    setBrandColor(org.brand_primary_color ?? "");
    setEmailSubjectTpl(org.quote_email_subject_template ?? "");
    setEmailBodyTpl(org.quote_email_body_template ?? "");
    setOutputLanguage(org.output_language ?? "en");
    setDefaultRenderMode(org.default_render_mode ?? "itemized");
  }, [org]);

  function handleSave() {
    setError(null);
    // Validate deposit percent — accept empty (clear) or 0–100.
    let depositPct: number | null = null;
    if (depositPctText.trim() !== "") {
      const n = Number(depositPctText);
      if (!Number.isFinite(n) || n < 0 || n > 100) {
        setError("Deposit must be a whole number between 0 and 100.");
        return;
      }
      depositPct = n / 100;
    }

    // Validate reminder cadence + max — accept empty as the
    // current value, otherwise clamped integers within the
    // server's range. The server caps cadence at 30 days and
    // max at 10 reminders so we mirror those bounds here.
    const cadenceNum = Number(reminderCadenceText);
    if (
      reminderCadenceText.trim() === "" ||
      !Number.isInteger(cadenceNum) ||
      cadenceNum < 0 ||
      cadenceNum > 30
    ) {
      setError("Reminder cadence must be a whole number of days between 0 and 30.");
      return;
    }
    const maxNum = Number(reminderMaxText);
    if (reminderMaxText.trim() === "" || !Number.isInteger(maxNum) || maxNum < 0 || maxNum > 10) {
      setError("Reminder cap must be a whole number between 0 and 10.");
      return;
    }

    // Brand color: must be empty (= clear) or a valid #RRGGBB hex.
    // The server pattern-validates too, but catching it client-side
    // gives a friendlier error than a 422 toast.
    const trimmedColor = brandColor.trim();
    if (trimmedColor && !/^#[0-9A-Fa-f]{6}$/.test(trimmedColor)) {
      setError("Brand color must be in #RRGGBB form (e.g. #2B4A7C).");
      return;
    }

    update.mutate(
      {
        quote_header_tagline: tagline.trim() ? tagline.trim() : null,
        default_quote_terms: terms.trim() ? terms.trim() : null,
        default_deposit_pct: depositPct,
        quote_reminder_cadence_days: cadenceNum,
        quote_reminder_max: maxNum,
        business_phone: phone.trim() ? phone.trim() : null,
        business_email: businessEmail.trim() ? businessEmail.trim() : null,
        business_website: website.trim() ? website.trim() : null,
        business_address: address.trim() ? address.trim() : null,
        brand_primary_color: trimmedColor || null,
        quote_email_subject_template: emailSubjectTpl.trim() ? emailSubjectTpl.trim() : null,
        quote_email_body_template: emailBodyTpl.trim() ? emailBodyTpl.trim() : null,
        output_language: outputLanguage,
        default_render_mode: defaultRenderMode,
      },
      {
        onSuccess: () => {
          setSavedFlash(true);
          setTimeout(() => setSavedFlash(false), 2000);
        },
        onError: (err) => setError(err instanceof Error ? err.message : String(err)),
      },
    );
  }

  // Dirty-tracking: only enable Save if the form differs from what's
  // on the server. Trim before compare so trailing spaces don't read
  // as a real edit.
  const serverTagline = org?.quote_header_tagline ?? "";
  const serverTerms = org?.default_quote_terms ?? "";
  const serverDepositText =
    org?.default_deposit_pct != null ? Math.round(org.default_deposit_pct * 100).toString() : "";
  const serverCadenceText = (org?.quote_reminder_cadence_days ?? 3).toString();
  const serverMaxText = (org?.quote_reminder_max ?? 3).toString();
  const dirty =
    tagline.trim() !== serverTagline.trim() ||
    terms.trim() !== serverTerms.trim() ||
    depositPctText.trim() !== serverDepositText.trim() ||
    reminderCadenceText.trim() !== serverCadenceText ||
    reminderMaxText.trim() !== serverMaxText ||
    phone.trim() !== (org?.business_phone ?? "").trim() ||
    businessEmail.trim() !== (org?.business_email ?? "").trim() ||
    website.trim() !== (org?.business_website ?? "").trim() ||
    address.trim() !== (org?.business_address ?? "").trim() ||
    brandColor.trim() !== (org?.brand_primary_color ?? "").trim() ||
    emailSubjectTpl !== (org?.quote_email_subject_template ?? "") ||
    emailBodyTpl !== (org?.quote_email_body_template ?? "") ||
    outputLanguage !== (org?.output_language ?? "en") ||
    defaultRenderMode !== (org?.default_render_mode ?? "itemized");

  return (
    <div className="mt-6 border-t border-rule pt-5">
      <h3 className="font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft">
        Quote PDF defaults
      </h3>
      <p className="mt-1 max-w-prose text-xs text-graphite-soft">
        Optional. These appear on every quote PDF and the public quote view your homeowners see.
      </p>

      <div className="mt-4 space-y-4">
        {/* Tagline */}
        <div>
          <label
            htmlFor="branding-tagline"
            className="block font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft"
          >
            Header tagline
          </label>
          <input
            id="branding-tagline"
            type="text"
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
            maxLength={200}
            placeholder="Licensed + insured · NC #ABC123"
            className="mt-1 w-full max-w-lg rounded border border-rule bg-paper px-3 py-2 font-mono text-sm text-graphite focus:border-graphite focus:outline-none"
          />
          <p className="mt-1 font-mono text-[11px] text-graphite-faint">
            Shown under your company name. Up to 200 characters.
          </p>
        </div>

        {/* Default terms */}
        <div>
          <label
            htmlFor="branding-terms"
            className="block font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft"
          >
            Default quote terms
          </label>
          <textarea
            id="branding-terms"
            value={terms}
            onChange={(e) => setTerms(e.target.value)}
            maxLength={4000}
            rows={5}
            placeholder="Payment schedule, warranty, lien disclosures, &c."
            className="mt-1 w-full max-w-lg rounded border border-rule bg-paper px-3 py-2 font-mono text-sm text-graphite focus:border-graphite focus:outline-none"
          />
          <p className="mt-1 font-mono text-[11px] text-graphite-faint">
            Seeded into every new quote's terms field. You can still edit per-quote.
          </p>
        </div>

        {/* Deposit pct */}
        <div>
          <label
            htmlFor="branding-deposit"
            className="block font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft"
          >
            Deposit on acceptance
          </label>
          <div className="mt-1 flex max-w-lg items-center gap-2">
            <input
              id="branding-deposit"
              type="number"
              min={0}
              max={100}
              step={1}
              inputMode="numeric"
              value={depositPctText}
              onChange={(e) => setDepositPctText(e.target.value)}
              placeholder="30"
              className="w-24 rounded border border-rule bg-paper px-3 py-2 font-mono text-sm text-graphite focus:border-graphite focus:outline-none"
            />
            <span className="font-mono text-sm text-graphite-soft">%</span>
          </div>
          <p className="mt-1 font-mono text-[11px] text-graphite-faint">
            Renders as a "Deposit due on acceptance" line below the total. Leave blank to omit.
          </p>
        </div>

        {/* Business contact info — printed on the PDF letterhead. */}
        <div className="border-t border-rule pt-4">
          <h4 className="font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft">
            Business contact
          </h4>
          <p className="mt-1 max-w-prose text-xs text-graphite-soft">
            Optional. Each line that's filled in renders on the quote PDF letterhead. Empty fields
            are skipped.
          </p>
          <div className="mt-3 grid max-w-lg grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="branding-phone"
                className="block font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft"
              >
                Phone
              </label>
              <PhoneInput
                id="branding-phone"
                value={phone}
                onChange={setPhone}
                maxLength={40}
                placeholder="(704) 555-0100"
                className="mt-1 w-full rounded border border-rule bg-paper px-3 py-2 font-mono text-sm text-graphite focus:border-graphite focus:outline-none"
              />
            </div>
            <div>
              <label
                htmlFor="branding-business-email"
                className="block font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft"
              >
                Contact email
              </label>
              <input
                id="branding-business-email"
                type="email"
                value={businessEmail}
                onChange={(e) => setBusinessEmail(e.target.value)}
                maxLength={255}
                placeholder="hello@company.com"
                className="mt-1 w-full rounded border border-rule bg-paper px-3 py-2 font-mono text-sm text-graphite focus:border-graphite focus:outline-none"
              />
            </div>
            <div className="col-span-2">
              <label
                htmlFor="branding-website"
                className="block font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft"
              >
                Website
              </label>
              <input
                id="branding-website"
                type="url"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                maxLength={255}
                placeholder="https://company.com"
                className="mt-1 w-full rounded border border-rule bg-paper px-3 py-2 font-mono text-sm text-graphite focus:border-graphite focus:outline-none"
              />
            </div>
            <div className="col-span-2">
              <label
                htmlFor="branding-address"
                className="block font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft"
              >
                Address
              </label>
              <textarea
                id="branding-address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                maxLength={1000}
                rows={3}
                placeholder={"123 Main Street\nCharlotte, NC 28202"}
                className="mt-1 w-full rounded border border-rule bg-paper px-3 py-2 font-mono text-sm text-graphite focus:border-graphite focus:outline-none"
              />
              <p className="mt-1 font-mono text-[11px] text-graphite-faint">
                Multiple lines are allowed. Renders as-is.
              </p>
            </div>
          </div>
        </div>

        {/* Brand color — single hex input + native swatch. */}
        <div className="border-t border-rule pt-4">
          <h4 className="font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft">
            Brand color
          </h4>
          <p className="mt-1 max-w-prose text-xs text-graphite-soft">
            Used as the accent on quote PDFs (the underline in the header, dividers). Leave blank
            for the house yellow.
          </p>
          <div className="mt-3 flex max-w-lg items-center gap-3">
            <input
              type="color"
              aria-label="Brand color picker"
              value={/^#[0-9A-Fa-f]{6}$/.test(brandColor.trim()) ? brandColor.trim() : "#E8B547"}
              onChange={(e) => setBrandColor(e.target.value)}
              className="h-10 w-14 cursor-pointer rounded border border-rule bg-paper p-1"
            />
            <input
              id="branding-color"
              type="text"
              value={brandColor}
              onChange={(e) => setBrandColor(e.target.value)}
              maxLength={7}
              placeholder="#2B4A7C"
              className="w-32 rounded border border-rule bg-paper px-3 py-2 font-mono text-sm text-graphite focus:border-graphite focus:outline-none"
            />
            {brandColor.trim() && (
              <button
                type="button"
                onClick={() => setBrandColor("")}
                className="font-mono text-xs text-graphite-soft hover:text-graphite"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Quote-send email templates — subject + body. */}
        <div className="border-t border-rule pt-4">
          <h4 className="font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft">
            Quote email
          </h4>
          <p className="mt-1 max-w-prose text-xs text-graphite-soft">
            Override the email subject + body the client receives. Tokens render as their values:{" "}
            <code className="font-mono">{"{{org_name}}"}</code>,{" "}
            <code className="font-mono">{"{{client_first_name}}"}</code>,{" "}
            <code className="font-mono">{"{{job_title}}"}</code>,{" "}
            <code className="font-mono">{"{{share_url}}"}</code>,{" "}
            <code className="font-mono">{"{{custom_message}}"}</code>,{" "}
            <code className="font-mono">{"{{sender_label}}"}</code>. Leave blank to use the built-in
            copy.
          </p>
          <div className="mt-3 space-y-3">
            <div>
              <label
                htmlFor="branding-email-subject"
                className="block font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft"
              >
                Subject
              </label>
              <input
                id="branding-email-subject"
                type="text"
                value={emailSubjectTpl}
                onChange={(e) => setEmailSubjectTpl(e.target.value)}
                maxLength={255}
                placeholder="Quote from {{org_name}}: {{job_title}}"
                className="mt-1 w-full max-w-lg rounded border border-rule bg-paper px-3 py-2 font-mono text-sm text-graphite focus:border-graphite focus:outline-none"
              />
            </div>
            <div>
              <label
                htmlFor="branding-email-body"
                className="block font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft"
              >
                Body
              </label>
              <textarea
                id="branding-email-body"
                value={emailBodyTpl}
                onChange={(e) => setEmailBodyTpl(e.target.value)}
                maxLength={8000}
                rows={8}
                placeholder={
                  "Hi {{client_first_name}},\n\n" +
                  "Thanks for the chance to bid on {{job_title}}. " +
                  "Quote attached and viewable here:\n\n" +
                  "{{share_url}}\n\n" +
                  "—\n{{sender_label}}"
                }
                className="mt-1 w-full max-w-lg rounded border border-rule bg-paper px-3 py-2 font-mono text-sm text-graphite focus:border-graphite focus:outline-none"
              />
              <p className="mt-1 font-mono text-[11px] text-graphite-faint">
                The "View the quote" button is appended automatically — you don't have to embed it.
              </p>
            </div>
          </div>
        </div>

        {/* Default customer view (render mode) */}
        <div className="border-t border-rule pt-4">
          <h4 className="font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft">
            Default customer view
          </h4>
          <p className="mt-1 max-w-prose text-xs text-graphite-soft">
            How new quotes render to the homeowner. Per-quote toggle on the editor still
            overrides this default. Internal cost build-up never changes — only the customer-
            facing surface (public quote link + email PDF).
          </p>
          <div className="mt-3 inline-flex rounded-lg border border-rule bg-paper p-1">
            <button
              type="button"
              onClick={() => setDefaultRenderMode("itemized")}
              className={
                "rounded px-3 py-1.5 font-mono text-xs font-semibold transition " +
                (defaultRenderMode === "itemized"
                  ? "bg-graphite text-paper"
                  : "text-graphite-soft hover:text-graphite")
              }
              aria-pressed={defaultRenderMode === "itemized"}
            >
              Itemized
            </button>
            <button
              type="button"
              onClick={() => setDefaultRenderMode("artisan")}
              className={
                "rounded px-3 py-1.5 font-mono text-xs font-semibold transition " +
                (defaultRenderMode === "artisan"
                  ? "bg-graphite text-paper"
                  : "text-graphite-soft hover:text-graphite")
              }
              aria-pressed={defaultRenderMode === "artisan"}
            >
              Artisan
            </button>
          </div>
          <p className="mt-2 max-w-prose font-mono text-[11px] text-graphite-faint">
            <strong>Itemized:</strong> homeowner sees the full line-item table.{" "}
            <strong>Artisan:</strong> homeowner sees the project description + grand total
            only — the way experienced remodelers ship a bid.
          </p>
        </div>

        {/* Output language */}
        <div className="border-t border-rule pt-4">
          <h4 className="font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft">
            Quote output language
          </h4>
          <p className="mt-1 max-w-prose text-xs text-graphite-soft">
            What language quote line items, scope summaries, and terms come out in. The walkthrough
            audio transcript stays in the source language regardless of this setting — only the
            generated copy your client sees is constrained.
          </p>
          <div className="mt-3">
            <label
              htmlFor="branding-output-language"
              className="block font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft"
            >
              Language
            </label>
            <select
              id="branding-output-language"
              value={outputLanguage}
              onChange={(e) => setOutputLanguage(e.target.value)}
              className="mt-1 w-full max-w-xs rounded border border-rule bg-paper px-3 py-2 font-mono text-sm text-graphite focus:border-graphite focus:outline-none"
            >
              <option value="en">English</option>
              <option value="es">Español (Spanish)</option>
              <option value="fr">Français (French)</option>
              <option value="pt">Português (Portuguese)</option>
              <option value="de">Deutsch (German)</option>
              <option value="it">Italiano (Italian)</option>
            </select>
            <p className="mt-1 font-mono text-[11px] text-graphite-faint">
              Useful when subs record walkthroughs in one language but your homeowner clients read
              another. Default: English.
            </p>
          </div>
        </div>

        {/* Reminder cadence + cap */}
        <div className="border-t border-rule pt-4">
          <h4 className="font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft">
            Auto-reminders
          </h4>
          <p className="mt-1 max-w-prose text-xs text-graphite-soft">
            Resends sent-but-unanswered quotes on a cadence. The client gets a fresh share link each
            time; the contractor doesn't have to remember to chase.
          </p>
          <div className="mt-3 grid max-w-lg grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="branding-reminder-cadence"
                className="block font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft"
              >
                Cadence
              </label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  id="branding-reminder-cadence"
                  type="number"
                  min={0}
                  max={30}
                  step={1}
                  inputMode="numeric"
                  value={reminderCadenceText}
                  onChange={(e) => setReminderCadenceText(e.target.value)}
                  className="w-20 rounded border border-rule bg-paper px-3 py-2 font-mono text-sm text-graphite focus:border-graphite focus:outline-none"
                />
                <span className="font-mono text-sm text-graphite-soft">days</span>
              </div>
              <p className="mt-1 font-mono text-[11px] text-graphite-faint">
                Days between reminders. <strong>0</strong> disables.
              </p>
            </div>

            <div>
              <label
                htmlFor="branding-reminder-max"
                className="block font-mono text-xs font-semibold uppercase tracking-wider text-graphite-soft"
              >
                Max reminders
              </label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  id="branding-reminder-max"
                  type="number"
                  min={0}
                  max={10}
                  step={1}
                  inputMode="numeric"
                  value={reminderMaxText}
                  onChange={(e) => setReminderMaxText(e.target.value)}
                  className="w-20 rounded border border-rule bg-paper px-3 py-2 font-mono text-sm text-graphite focus:border-graphite focus:outline-none"
                />
                <span className="font-mono text-sm text-graphite-soft">total</span>
              </div>
              <p className="mt-1 font-mono text-[11px] text-graphite-faint">
                Stop after this many reminders per quote.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={handleSave}
          disabled={!dirty || update.isPending}
          className="rounded bg-graphite px-4 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-40"
        >
          {update.isPending ? "Saving…" : "Save defaults"}
        </button>
        {savedFlash && (
          <span className="inline-flex items-center gap-1 font-mono text-xs text-graphite-soft">
            <CheckCircle2 className="h-3.5 w-3.5 text-graphite" aria-hidden />
            Saved
          </span>
        )}
        {error && <span className="font-mono text-xs text-red-pencil">{error}</span>}
      </div>

      {/* Org-config bundle editors. Each one PATCHes its own slice
          of the org's JSON config (payment-terms rules, brand
          preferences, default disclaimers, payment methods)
          independently. Lives below the existing branding form so
          the contractor encounters them after the basics. */}
      <OrgConfigBundles orgId={orgId} org={org} />
    </div>
  );
}

// ---------------------------------------------------------------
// Upgrade-success banner — shown when ?checkout=success is in the URL
// ---------------------------------------------------------------

/** Celebrates the upgrade and (when no logo is on file) anchor-links
 *  down to the BrandingCard. Auto-dismisses if the user clicks away.
 *
 *  We re-fetch ``/organizations/{id}`` once on mount so the banner's
 *  has-logo decision is fresh; the Stripe webhook may have just
 *  flipped ``plan_tier`` and the cache is stale. */
function UpgradeSuccessBanner({
  orgId: _orgId,
  status,
  hasLogo,
}: {
  orgId: string;
  status: SubscriptionStatusResponse;
  hasLogo: boolean | undefined;
}) {
  const [dismissed, setDismissed] = useState(false);

  // ``pencilout_return_to_app`` is set by MagicLinkConsumePage when
  // the user came from the iOS upgrade-handoff flow. Read once on
  // mount so we know whether to offer the "Open in PencilOut" CTA.
  // We DON'T clear it here — clear after the user actually clicks
  // it (or after they explicitly dismiss this banner) so refreshing
  // the page mid-flow doesn't lose the return path.
  const cameFromIOS = (() => {
    try {
      return window.sessionStorage.getItem("pencilout_return_to_app") === "1";
    } catch {
      return false;
    }
  })();

  function clearReturnFlag() {
    try {
      window.sessionStorage.removeItem("pencilout_return_to_app");
    } catch {
      /* non-fatal */
    }
  }

  // Strip the ?checkout=success param after a few seconds so refresh
  // doesn't re-show the banner forever. We don't navigate — just
  // replace the URL so router state is preserved.
  useEffect(() => {
    const t = setTimeout(() => {
      const url = new URL(window.location.href);
      url.searchParams.delete("checkout");
      window.history.replaceState({}, "", url.toString());
    }, 4000);
    return () => clearTimeout(t);
  }, []);

  if (dismissed) return null;

  const tier = status.plan_tier ?? "your new plan";
  const pretty = tier === "free" ? "Free" : tier.charAt(0).toUpperCase() + tier.slice(1);
  const promptLogo = hasLogo === false && (tier === "pro" || tier === "crew");

  return (
    <div className="rounded-lg border border-pencil-yellow bg-pencil-yellow-soft p-4">
      <div className="flex flex-wrap items-start gap-3">
        <PartyPopper className="mt-0.5 h-5 w-5 flex-none text-graphite" aria-hidden />
        <div className="flex-1 text-sm">
          <p className="font-semibold">You're on {pretty}. Welcome.</p>
          {cameFromIOS ? (
            <p className="mt-1 text-graphite-soft">
              Your subscription is active and your usage meter has been reset. Tap below to jump
              back into the PencilOut app — your new plan will be reflected there immediately.
              {promptLogo && (
                <>
                  {" "}
                  Or{" "}
                  <a
                    href="/settings"
                    className="font-semibold text-graphite underline underline-offset-2 hover:text-graphite-soft"
                  >
                    upload your logo →
                  </a>{" "}
                  first so your branded PDFs are ready.
                </>
              )}
            </p>
          ) : promptLogo ? (
            <p className="mt-1 text-graphite-soft">
              One more thing — upload your logo so it appears on the branded quote PDFs your
              homeowners receive.{" "}
              <a
                href="/settings"
                className="font-semibold text-graphite underline underline-offset-2 hover:text-graphite-soft"
              >
                Upload now →
              </a>
            </p>
          ) : (
            <p className="mt-1 text-graphite-soft">
              Your subscription is active and your usage meter has been reset.
            </p>
          )}

          {cameFromIOS && (
            <a
              href="pencilout://billing/upgraded"
              onClick={clearReturnFlag}
              className="mt-3 inline-flex items-center gap-2 rounded bg-graphite px-3 py-1.5 font-mono text-xs font-semibold text-paper transition hover:bg-graphite-soft"
            >
              ← Return to PencilOut app
            </a>
          )}
        </div>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => {
            setDismissed(true);
            // User chose to stay on web; clear the flag so a future
            // refresh (or a back-button + refresh) doesn't keep
            // pestering them with the return-to-app CTA.
            clearReturnFlag();
          }}
          className="rounded p-1 text-graphite-soft transition hover:bg-paper hover:text-graphite"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
